/**
 * useSessionManagement Hook
 * Provides session management functionality and monitoring
 */

import { useEffect, useCallback, useRef } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import SESSION from "@/redux/Session";
import { SelectLoggedUser } from "@/redux/Auth";
import { toast } from "react-toastify";

interface UseSessionManagementOptions {
  autoCheck?: boolean;
  checkInterval?: number; // in milliseconds
  enableActivityTracking?: boolean;
  activityInterval?: number; // in milliseconds
}

/**
 * Helper function to check if user is authenticated
 */
const isUserAuthenticated = (): boolean => {
  if (typeof window === "undefined") return false;
  return !!localStorage.getItem("Education_User_token");
};

/**
 * Hook for managing user sessions
 */
export const useSessionManagement = (
  options: UseSessionManagementOptions = {},
) => {
  const {
    autoCheck = true,
    checkInterval = 5 * 60 * 1000, // 5 minutes
    enableActivityTracking = true,
    activityInterval = 1 * 60 * 1000, // 1 minute
  } = options;

  const dispatch = useAppDispatch();
  const sessionState = useAppSelector((state: any) => state.session);
  const loggedUser = useAppSelector(SelectLoggedUser);

  // Throttle activity updates to prevent excessive requests
  const lastActivityTimeRef = useRef<number>(0);
  const ACTIVITY_THROTTLE_MS = 30000; // Only allow one activity update per 30 seconds

  /**
   * Fetch active sessions
   */
  const fetchActiveSessions = useCallback(async () => {
    try {
      await dispatch(SESSION.thunks.fetchActiveSessions());
    } catch (error) {
      console.error("Failed to fetch active sessions:", error);
    }
  }, [dispatch]);

  /**
   * Check for account sharing
   */
  const checkAccountSharing = useCallback(async () => {
    try {
      const result = await dispatch(SESSION.thunks.checkAccountSharing());
      if (result.payload?.data?.is_suspicious) {
        // toast.warning("تحذير: تم كشف نشاط غير عادي على حسابك");
      }
      return result.payload;
    } catch (error) {
      console.error("Failed to check account sharing:", error);
    }
  }, [dispatch]);

  /**
   * Logout from specific device
   */
  const logoutFromDevice = useCallback(
    async (sessionId: string | number) => {
      try {
        const result = await dispatch(
          SESSION.thunks.logoutFromDevice(sessionId),
        );
        if (result.payload?.success !== false) {
          toast.success("تم إنهاء الجلسة بنجاح");
          // Refresh the sessions list
          await fetchActiveSessions();
        }
        return result.payload;
      } catch (error) {
        console.error("Failed to logout from device:", error);
        toast.error("فشل إنهاء الجلسة");
      }
    },
    [dispatch, fetchActiveSessions],
  );

  /**
   * Logout from all other devices
   */
  const logoutAllOtherDevices = useCallback(async () => {
    try {
      const result = await dispatch(SESSION.thunks.logoutAllOtherDevices());
      if (result.payload?.success !== false) {
        toast.success("تم إنهاء جميع الجلسات الأخرى بنجاح");
        // Refresh the sessions list
        await fetchActiveSessions();
      }
      return result.payload;
    } catch (error) {
      console.error("Failed to logout from other devices:", error);
      toast.error("فشل إنهاء الجلسات");
    }
  }, [dispatch, fetchActiveSessions]);

  /**
   * Update session activity
   */
  const updateActivity = useCallback(async () => {
    try {
      await dispatch(SESSION.thunks.updateSessionActivity());
    } catch (error) {
      // Silent fail for activity updates
      console.debug("Activity update failed:", error);
    }
  }, [dispatch]);

  /**
   * Set up automatic checking
   * Only performs checks if user is authenticated
   */
  useEffect(() => {
    if (!autoCheck) return;

    // Don't perform checks if user is not authenticated
    if (!loggedUser?.userToken) return;

    // Initial check
    checkAccountSharing();
    fetchActiveSessions();

    // Set up periodic checks
    const checkInterval_ = setInterval(() => {
      // Re-verify authentication before each periodic check
      if (!loggedUser?.userToken) {
        clearInterval(checkInterval_);
        return;
      }
      checkAccountSharing();
    }, checkInterval);

    return () => {
      clearInterval(checkInterval_);
    };
  }, [
    autoCheck,
    checkInterval,
    checkAccountSharing,
    fetchActiveSessions,
    loggedUser?.userToken,
  ]);

  /**
   * Set up activity tracking
   * Only tracks activity if user is authenticated
   * Throttled to prevent excessive API requests
   */
  useEffect(() => {
    if (!enableActivityTracking) return;

    // Don't track if user is not authenticated
    if (!loggedUser?.userToken) return;

    // Update activity on user interaction with throttling
    const handleActivity = () => {
      if (!loggedUser?.userToken) return;

      const now = Date.now();
      // Only send activity update if last update was more than ACTIVITY_THROTTLE_MS ago
      if (now - lastActivityTimeRef.current >= ACTIVITY_THROTTLE_MS) {
        lastActivityTimeRef.current = now;
        updateActivity();
      }
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((event) => {
      document.addEventListener(event, handleActivity);
    });

    // Also set up periodic activity updates (independent of throttle)
    const activityTimer = setInterval(() => {
      if (loggedUser?.userToken) {
        lastActivityTimeRef.current = Date.now();
        updateActivity();
      }
    }, activityInterval);

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      clearInterval(activityTimer);
    };
  }, [
    enableActivityTracking,
    activityInterval,
    updateActivity,
    loggedUser?.userToken,
  ]);

  return {
    // State
    activeSessions: sessionState.activeSessions,
    sharingDetected: sessionState.sharingDetected,
    anomalies: sessionState.anomalies,
    loading: sessionState.loading,
    error: sessionState.error,
    isMonitoring: sessionState.isMonitoring,
    lastChecked: sessionState.lastChecked,

    // Actions
    fetchActiveSessions,
    checkAccountSharing,
    logoutFromDevice,
    logoutAllOtherDevices,
    updateActivity,
  };
};

export default useSessionManagement;
